import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import { buildRoute } from '@/shared/lib/constants';
import type { MorningSummaryActivitySummary } from '../types';

interface YesterdayActivityCardProps {
  farmId: string;
  activity: MorningSummaryActivitySummary;
  totalPonds: number;
}

interface ActivityItemProps {
  label: string;
  completed: number;
  total: number;
  href: string;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ label, completed, total, href }) => {
  const percentage = total > 0 ? (completed / total) * 100 : 0;

  // Color based on completion percentage
  const getBarColor = (): string => {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <Link
      to={href}
      className="block p-3 rounded-lg border border-slate-200/50 hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-500">{completed}/{total} ponds</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${getBarColor()}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </Link>
  );
};

export const YesterdayActivityCard: React.FC<YesterdayActivityCardProps> = ({
  farmId,
  activity,
  totalPonds,
}) => {
  const activities = [
    {
      label: 'Water Quality Tests',
      completed: activity.waterQualityTests.completed,
      total: activity.waterQualityTests.total || totalPonds,
      href: buildRoute.waterQuality(farmId),
    },
    {
      label: 'Feed Records',
      completed: activity.feedRecords.completed,
      total: activity.feedRecords.total || totalPonds,
      href: buildRoute.feed(farmId),
    },
    {
      label: 'Health Checks',
      completed: activity.healthChecks.completed,
      total: activity.healthChecks.total || totalPonds,
      href: buildRoute.fishHealth(farmId),
    },
    {
      label: 'Maintenance Checks',
      completed: activity.maintenanceChecks.completed,
      total: activity.maintenanceChecks.total || totalPonds,
      href: buildRoute.maintenance(farmId),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-500" />
          <CardTitle>Yesterday's Activity</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activities.map((item) => (
            <ActivityItem
              key={item.label}
              label={item.label}
              completed={item.completed}
              total={item.total}
              href={item.href}
            />
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-4 text-center">
          Click on any activity to view details
        </p>
      </CardContent>
    </Card>
  );
};
